/**
 * Footer Component
 * 
 * Phase 3 Optimization:
 * - Removed Framer Motion dependency
 * - Using CSS hover effects for scroll button
 * - Improved touch targets for mobile
 */

import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguageStore } from "@/stores/language";

export const Footer = () => {
  const { language, t } = useLanguageStore();
  
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <footer className="border-t border-foreground/10 bg-background py-16 md:py-24">
      <div className="container mx-auto px-6">
        {/* Main footer grid */}
        <div className="grid md:grid-cols-4 gap-12 md:gap-8 mb-16">
          {/* Brand column */}
          <div className="md:col-span-2">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">FSHN AI</h2>
            <p className="text-muted-foreground max-w-sm mb-6">
              {t(
                "AI-powered fashion prompt collections for creators. Professional editorial imagery, instantly.",
                "Colecciones de prompts de moda impulsados por IA para creadores. Imágenes editoriales profesionales, al instante."
              )}
            </p>
          </div>

          {/* Navigation column */}
          <div>
            <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-6">
              {t("Navigation", "Navegación")}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/shop" 
                  className="text-foreground hover:text-muted-foreground transition-colors touch-action-manipulation inline-block py-1"
                >
                  {t("Shop", "Tienda")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/faq" 
                  className="text-foreground hover:text-muted-foreground transition-colors touch-action-manipulation inline-block py-1"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-foreground hover:text-muted-foreground transition-colors touch-action-manipulation inline-block py-1"
                >
                  {t("Let's Talk", "Hablemos")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-6">
              {t("Contact", "Contacto")}
            </h3>
            <a 
              href="mailto:admin@fshn.institute" 
              className="text-foreground hover:text-accent transition-colors flex items-center gap-2 group touch-action-manipulation py-1"
            >
              admin@fshn.institute
              <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

        {/* Bottom section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-foreground/10">
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-muted-foreground">
            <p>© {currentYear} Melissa Restrepo Vidal — CHIMERA™</p>
            <span className="hidden md:inline">·</span>
            <p className="text-xs text-center md:text-left max-w-xl">
              {t(
                "CHIMERA™ is a proprietary prompt system by Melissa Restrepo Vidal. Prompt files are licensed, not sold.",
                "CHIMERA™ es un sistema de prompts propietario de Melissa Restrepo Vidal. Los archivos de prompts son licenciados, no vendidos."
              )}
            </p>
          </div>

          {/* Phase 3: Replaced motion.button with CSS hover */}
          <button
            onClick={scrollToTop}
            className="text-sm font-mono text-muted-foreground hover:text-foreground transition-colors hover-lift touch-action-manipulation min-h-[44px] px-2"
            aria-label={t("Scroll back to top of page", "Volver arriba de la página")}
          >
            {t("Back to top", "Volver arriba")} ↑
          </button>
        </div>
      </div>
    </footer>
  );
};
